import { useCallback, useEffect, useRef, RefObject } from 'react';
import { MAX_HEIGHT_PERCENTAGE } from './ChatInput.constants';

interface UseAutoResizeTextareaResult {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  adjustHeight: VoidFunction;
  resetHeight: VoidFunction;
}

const useAutoResizeTextarea = (drawerRef?: RefObject<HTMLElement | null>): UseAutoResizeTextareaResult => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const getMaxHeight = useCallback((): number => {
    const drawerHeight = drawerRef?.current?.clientHeight ?? window.innerHeight;
    return drawerHeight * (MAX_HEIGHT_PERCENTAGE / 100);
  }, [drawerRef]);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const maxHeight = getMaxHeight();
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [getMaxHeight]);

  const resetHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  return { textareaRef, adjustHeight, resetHeight };
};

export default useAutoResizeTextarea;
